import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useShallow } from 'zustand/shallow';
import { IconButton } from '@mui/material';
import { Icon } from '@sdb/planning-ui';

import {
  formatEntityDisplayLabel,
  getEntityIcon
} from 'components/mrp/mrp.utils';

import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import { DateNavigation } from 'components/shared/index';
import EntitySearch from 'components/mrp/components/EntitySearch/EntitySearch';

import {
  computeRange,
  toDate
} from 'components/shared/DateNavigation/DateNavigation.utils';
import { useDateNavigationHoursStore } from 'components/shared/DateNavigation/DateNavigationHours.stores';
import { useDateNavigationStore } from 'components/shared/DateNavigation/DateNavigation.stores';

import { EventResponse, PlanningEntityIdentifier } from 'generated/index';
import { SingleOrRange } from 'components/shared/DateNavigation';
import { GridRowId, GridRowSelectionModel } from '@mui/x-data-grid-pro';

import {
  AppointmentsOverviewDateNavigationContainer,
  AppointmentsOverviewFiltersContainer,
  AppointmentsOverviewHeader,
  AppointmentsOverviewPageContainer,
  AppointmentsOverviewParticipantFilter,
  AppointmentsOverviewSelectedParticipant,
  AppointmentsOverviewSelectedParticipantLabel
} from './AppointmentsOverview.styles';
import {
  getAppointmentRowId,
  useColumnDefinitions
} from './AppointmentsOverview.utils';
import { useFilteredAppointments } from './hooks/useFilteredAppointments';
import AppointmentRowTooltip from './components/AppointmentRowTooltip';

const PARTICIPANT_ENTITY_TYPES: PlanningEntityIdentifier['planningEntityType'][] =
  ['EMPLOYEE', 'CLIENT', 'RESOURCE'];

type SelectedParticipant = {
  planningEntity: PlanningEntityIdentifier;
  label: string;
};

const AppointmentsOverview = () => {
  const { t } = useTranslation('common');

  const {
    selectedDate,
    selectedDateRange,
    setSelectedDate,
    setSelectedDateRange
  } = useDateNavigationHoursStore(
    useShallow((state) => ({
      selectedDate: state.selectedDate,
      selectedDateRange: state.selectedDateRange,
      setSelectedDate: state.setSelectedDate,
      setSelectedDateRange: state.setSelectedDateRange
    }))
  );

  const period = useDateNavigationStore(useShallow((state) => state.period));
  const appliedPeriod = period ?? 'day';

  const [selectedParticipant, setSelectedParticipant] =
    useState<SelectedParticipant | null>(null);

  const { startDate, endDate } = useMemo(() => {
    if (appliedPeriod === 'custom') {
      return computeRange(selectedDateRange ?? null, appliedPeriod);
    }
    return computeRange(selectedDate, appliedPeriod);
  }, [appliedPeriod, selectedDate, selectedDateRange]);

  const {
    data = [],
    isLoading,
    isError,
    refetch
  } = useFilteredAppointments({
    startDate,
    endDate,
    planningEntity: selectedParticipant?.planningEntity
  });

  const [selectedAppointments, setSelectedAppointments] = useState<
    EventResponse[]
  >([]);

  const selectionModel = useMemo<GridRowSelectionModel>(
    () => ({
      type: 'include',
      ids: new Set<GridRowId>(
        selectedAppointments.map((a) => `${a.id}-${a.date}`)
      )
    }),
    [selectedAppointments]
  );

  // Clear selection when the filter context changes
  useEffect(() => {
    setSelectedAppointments([]);
  }, [
    selectedParticipant?.planningEntity.entityId,
    selectedParticipant?.planningEntity.planningEntityType,
    startDate,
    endDate
  ]);

  const handleSelectedDateChange = useCallback<(date: SingleOrRange) => void>(
    (date) => {
      if (appliedPeriod === 'custom') {
        setSelectedDateRange(Array.isArray(date) ? date : undefined);
        return;
      }

      if (Array.isArray(date) || !date) {
        setSelectedDate(null);
        return;
      }

      setSelectedDate(toDate(date));
    },
    [appliedPeriod, setSelectedDate, setSelectedDateRange]
  );

  const handleEntityChange = useCallback(
    (entity: PlanningEntityIdentifier, label: string) => {
      setSelectedParticipant({ planningEntity: entity, label });
    },
    []
  );

  const handleClearParticipant = useCallback(() => {
    setSelectedParticipant(null);
  }, []);

  const handleSelectionChange = useCallback(
    (selectionModel: GridRowSelectionModel) => {
      const selected = data.filter((a) =>
        selectionModel.ids.has(`${a.id}-${a.date}`)
      );
      setSelectedAppointments(selected);
    },
    [data]
  );

  const columns = useColumnDefinitions();

  const dataById = useMemo(
    () => new Map(data.map((a) => [`${a.id}-${a.date}`, a])),
    [data]
  );

  const defaultDate = useMemo(
    () => (selectedDate ? selectedDate : dayjs().startOf('day').toDate()),
    [selectedDate]
  );

  return (
    <AppointmentsOverviewPageContainer>
      <AppointmentsOverviewHeader>
        <AppointmentsOverviewFiltersContainer>
          <AppointmentsOverviewParticipantFilter>
            {selectedParticipant ? (
              <AppointmentsOverviewSelectedParticipant>
                {getEntityIcon(
                  selectedParticipant.planningEntity.planningEntityType,
                  undefined,
                  'MEDIUM',
                  true
                )}
                <AppointmentsOverviewSelectedParticipantLabel>
                  {formatEntityDisplayLabel(
                    selectedParticipant.label,
                    selectedParticipant.planningEntity.number
                  )}
                </AppointmentsOverviewSelectedParticipantLabel>
                <IconButton
                  size="small"
                  onClick={handleClearParticipant}
                  aria-label={t(
                    'appointmentsOverview.selectedParticipantLabel'
                  )}
                >
                  <Icon type="xmark" fontSize="small" />
                </IconButton>
              </AppointmentsOverviewSelectedParticipant>
            ) : (
              <EntitySearch
                filter={PARTICIPANT_ENTITY_TYPES}
                entityTypeFilters={PARTICIPANT_ENTITY_TYPES}
                multiple={false}
                minWidth={360}
                onChange={handleEntityChange}
              />
            )}
          </AppointmentsOverviewParticipantFilter>

          <AppointmentsOverviewDateNavigationContainer>
            <DateNavigation
              allowMenuOptions
              defaultDate={defaultDate}
              defaultDateRange={selectedDateRange}
              defaultPeriod="day"
              onDateChange={handleSelectedDateChange}
              options={['day', 'week', 'month', 'custom']}
            />
          </AppointmentsOverviewDateNavigationContainer>
        </AppointmentsOverviewFiltersContainer>
      </AppointmentsOverviewHeader>

      <TableCRUDLayout
        columns={columns}
        rows={data}
        loading={isLoading}
        hasLoadingError={isError}
        onLoadingError={refetch}
        getRowId={getAppointmentRowId}
        translationKey={
          selectedParticipant
            ? undefined
            : 'appointmentsOverview.selectParticipantHint'
        }
        initialState={{
          sorting: {
            sortModel: [{ field: 'appointmentDate', sort: 'asc' }]
          }
        }}
        editable={false}
        hideCreateAction
        checkboxSelection
        customSelectionModel={selectionModel}
        onSelectionChange={handleSelectionChange}
        slots={{
          row: (gridRow) => (
            <AppointmentRowTooltip
              event={dataById.get(`${gridRow.row?.id}-${gridRow.row?.date}`)}
              gridRow={gridRow}
            />
          )
        }}
      />
    </AppointmentsOverviewPageContainer>
  );
};

export default AppointmentsOverview;
